import React from 'react';
import './ErrorHandler.css';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';

const ErrorHandler = ({ error, onRetry, onClear }) => {
  // Determine error type and customize message
  const getErrorInfo = () => {
    const errorMessage = error?.message || String(error) || 'An unknown error occurred';
    
    // Timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('ECONNABORTED')) {
      return {
        type: 'timeout',
        icon: <AccessTimeIcon fontSize="inherit" />,
        title: 'Query Timeout',
        message: 'Your query took too long to process. This might be due to complex data analysis or high server load.',
        suggestions: [
          'Try a simpler query with fewer conditions',
          'Narrow down your date range or scope',
          'Break complex queries into smaller parts'
        ]
      };
    }
    
    // Network errors
    if (errorMessage.includes('Network') || errorMessage.includes('ECONNREFUSED')) {
      return {
        type: 'network',
        icon: <PublicIcon fontSize="inherit" />,
        title: 'Connection Error',
        message: 'Unable to connect to the analytics server. Please check your connection.',
        suggestions: [
          'Check your internet connection',
          'Try refreshing the page',
          'Contact support if the problem persists'
        ]
      };
    }
    
    // Query generation errors
    if (errorMessage.includes('SQL') || errorMessage.includes('query')) {
      return {
        type: 'query',
        icon: <HelpOutlineIcon fontSize="inherit" />,
        title: 'Query Error',
        message: 'We had trouble understanding your question or generating the appropriate database query.',
        suggestions: [
          'Try rephrasing your question in simpler terms',
          'Be more specific about what data you\'re looking for',
          'Use common business terms and metrics'
        ]
      };
    }
    
    // Server errors
    if (errorMessage.includes('500') || errorMessage.includes('Internal Server')) {
      return {
        type: 'server',
        icon: <BuildIcon fontSize="inherit" />,
        title: 'Server Error',
        message: 'There was an internal server error while processing your request.',
        suggestions: [
          'Wait a moment and try again',
          'Try a different query',
          'Contact support if the error continues'
        ]
      };
    }
    
    // Data not found
    if (errorMessage.includes('not found') || errorMessage.includes('404')) {
      return {
        type: 'notfound',
        icon: <SearchIcon fontSize="inherit" />,
        title: 'No Data Found',
        message: 'We couldn\'t find any data matching your query criteria.',
        suggestions: [
          'Check your spelling and product names',
          'Try broader search terms',
          'Expand your date range'
        ]
      };
    }
    
    // Generic error
    return {
      type: 'generic',
      icon: <WarningAmberIcon fontSize="inherit" />,
      title: 'Something went wrong',
      message: errorMessage,
      suggestions: [
        'Try again in a moment',
        'Simplify your query',
        'Contact support if the problem continues'
      ]
    };
  };

  const errorInfo = getErrorInfo();

  return (
    <div className="error-handler">
      <div className="error-container">
        <div className="error-header">
          <div className="error-icon">{errorInfo.icon}</div>
          <div className="error-content">
            <h3 className="error-title">{errorInfo.title}</h3>
            <p className="error-message">{errorInfo.message}</p>
          </div>
        </div>

        {errorInfo.suggestions && (
          <div className="error-suggestions">
            <h4 className="suggestions-title">
              <LightbulbIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Try this:
            </h4>
            <ul className="suggestions-list">
              {errorInfo.suggestions.map((suggestion, index) => (
                <li key={index} className="suggestion-item">
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="error-actions">
          <button 
            className="action-button primary"
            onClick={onRetry}
          >
            <RefreshIcon style={{ marginRight: 6 }} />
            Try Again
          </button>
          <button 
            className="action-button secondary"
            onClick={onClear}
          >
            <CloseIcon style={{ marginRight: 6 }} />
            Clear
          </button>
          <button 
            className="action-button secondary"
            onClick={() => window.open('mailto:armaan@gottea.in?subject=Analytics Dashboard Error', '_blank')}
          >
            <EmailIcon style={{ marginRight: 6 }} />
            Contact Support
          </button>
        </div>

        {/* Technical details (collapsible) */}
        <details className="error-details">
          <summary>Technical Details</summary>
          <pre className="error-stack">
            {JSON.stringify(error, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default ErrorHandler; 